import axios from "axios";
import { tokenStorage } from "../utils/tokenStorage";
import { toast } from "react-toastify";

const domain = window.location.hostname;
let baseURL = "";
let aggregate = {};

if (domain === process.env.REACT_APP_WEB_PARTS_URL) {
  baseURL = process.env.REACT_APP_PARTS_URL;
} else if (domain === process.env.REACT_APP_WEB_ACCESSORIES_URL) {
  baseURL = process.env.REACT_APP_ACCESSORIES_URL;
  aggregate = { aggregate: "unique_identifier" };
} else if (domain === process.env.REACT_APP_WEB_MOBILE_URL) {
  baseURL = process.env.REACT_APP_API_URL;
} else if (domain === process.env.REACT_APP_WEB_ACCESSORIES_GODOWN_URL){
  baseURL = process.env.REACT_APP_ACCESSORIES_GODDOWN_URL;
} else if (domain === process.env.REACT_APP_LOCAL_URL) {
  baseURL = process.env.REACT_APP_API_URL;
}

// Create axios instance
const apiClient = axios.create({
  baseURL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Store baseURL for use in interceptors
const API_BASE_URL = baseURL;

// Request interceptor to add auth token and domain-specific params
apiClient.interceptors.request.use(
  (config) => {
    const accessToken = tokenStorage.getAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    
    // Add aggregate parameter if defined
    if (Object.keys(aggregate).length > 0) {
      if (config.method === 'get' || config.method === 'delete') {
        config.params = { ...config.params, ...aggregate };
      } else if (config.method === 'post' || config.method === 'put' || config.method === 'patch') {
        if (config.data) {
          config.data = { ...config.data, ...aggregate };
        }
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Response interceptor to handle errors and token refresh
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = tokenStorage.getRefreshToken();

      if (refreshToken) {
        try {
          // Call refresh token API directly to avoid circular dependency
          const response = await axios.post(`${API_BASE_URL}/auth/admin/`, {
            refresh_token: refreshToken,
          });

          const data = response.data;
          const newAccessToken =
            data?.access ||
            data?.access_token ||
            data?.accessToken ||
            data?.token;
          const newRefreshToken =
            data?.refresh || data?.refresh_token || data?.refreshToken;
          const user = data?.user || tokenStorage.getUser();

          if (newAccessToken) {
            // Update tokens in storage
            tokenStorage.setTokens(
              newAccessToken,
              newRefreshToken || refreshToken,
              user
            );

            // Update the authorization header for the original request
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

            // Process the queue with the new token
            processQueue(null, newAccessToken);

            isRefreshing = false;

            // Retry the original request
            return apiClient(originalRequest);
          } else {
            throw new Error("No access token received from refresh");
          }
        } catch (refreshError) {
          // Refresh failed - clear tokens and redirect to login
          processQueue(refreshError, null);
          isRefreshing = false;

          tokenStorage.clearTokens();
          toast.error("Session expired. Please login again.");

          // Avoid redirect if already on login page
          if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }

          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token available - clear tokens and redirect
        isRefreshing = false;
        tokenStorage.clearTokens();
        toast.error("Session expired. Please login again.");

        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
